/** @sfdc-extension-file SNK */
import { type ReactElement } from 'react';
import type { ShopperProducts } from '@/scapi';
import { Link } from '@/components/link';
import logo from '/images/logo.svg';
import { useTranslation } from 'react-i18next';
import { EmbeddedComponentRegion } from '@/components/region/embedded-component-region';
import type { ComponentWithComponentData } from '@/lib/page-designer/component-loader.server';
import { MegaMenu, type MegaMenuNavItem } from '@/features/mega-menu';
import { Search } from '../search';
import { UserActions } from './ui/molecules/user-actions';

const MEGA_MENU_ITEMS: MegaMenuNavItem[] = [
    { id: 'brands', label: 'Brands', to: '/brands' },
    { id: 'new-in', label: 'New In', to: '/new-in' },
    { id: 'offers', label: 'Offers', to: '/offers' },
    { id: 'inside-space', label: 'Inside Space', to: '/inside-space' },
    { id: 'trending', label: 'Trending', to: '/trending' },
];

interface GlobalHeaderProps {
    rootCategory?: Promise<ShopperProducts.schemas['Category']>;
    /**
     * Embedded Layout.globalHeaderRegion from
     * `fetchComponentWithComponentData(..., { componentId: 'global-header-region' })`.
     * Renders the site-wide `announcement` region (e.g. Announcement Banner content blocks).
     */
    headerComponent?: Promise<ComponentWithComponentData | null>;
}

/**
 * Storefront header chrome. Banner content uses Site-Wide Regions / Embedded Content Blocks
 * on Layout.globalHeaderRegion (`announcement`) via Site-Wide Regions / ECB.
 *
 * @see https://developer.salesforce.com/docs/commerce/b2c-commerce/guide/sfnext-page-designer-content-blocks.html
 */
export const GlobalHeader = ({ rootCategory, headerComponent }: GlobalHeaderProps): ReactElement => {
    const { t } = useTranslation('header');

    return (
        <header className="sticky top-0 z-50 bg-snk-white" data-slot="global-header">
            <div className="flex justify-end px-4 lg:px-9">
                <EmbeddedComponentRegion
                    component={headerComponent}
                    regionId="announcement"
                    fallbackElement={null}
                    errorElement={null}
                />
            </div>
            <div className="flex flex-row flex-wrap w-full">
                <div className="flex-row flex justify-between w-full p-4">
                    <Link to="/" className="" data-testid="header-logo">
                        <img
                            src={logo}
                            alt={t('logoAlt')}
                            className="h-3 lg:h-4 w-auto [filter:var(--header-logo-filter)]"
                        />
                    </Link>
                    <Search />
                    <UserActions />
                </div>

                <div className="w-full mt-6">
                    <MegaMenu
                        data-slot="mega-menu"
                        items={MEGA_MENU_ITEMS}
                        submenuPanels={[]}
                        shopAllLabel="Shop app"
                        rootCategory={rootCategory}
                    />
                </div>
            </div>
        </header>
    );
};
